// Single source of truth for per-host runtime config. ONE production build is
// served from multiple hostnames (e.g. www.dncscrub.com and dnc.sonera.dev);
// the API base URL, SignalR URL, and cookie domain are chosen from
// window.location.hostname at load time — NOT baked in at build time.
//
// Local dev (`npm run dev`, served at spa.dncscrub.com) always uses the .env
// values so it talks to the local WebAPI, not production — hostname mapping
// only applies to production builds.

export type RuntimeConfig = {
  apiUrl: string;
  signalRUrl: string;
  cookieDomain: string;
  // Base URL of the legacy Tcl app for links and iframes (no trailing slash).
  classicUrl: string;
  // Base URL for XHR calls to Tcl endpoints (rpc/scrub, XFileCallbackPOST).
  // Kept separate from classicUrl: on dncscrub.com the SPA runs at www, so
  // calling www keeps these requests same-origin (no CORS needed there).
  tclApiUrl: string;
  // Base URL of the DNCScrubCore MVC app (no trailing slash).
  coreUrl: string;
  // Base URL of the data API (no trailing slash).
  dataApiUrl: string;
};

function envConfig(): RuntimeConfig {
  return {
    apiUrl: import.meta.env.VITE_APP_API_URL ?? "",
    signalRUrl: import.meta.env.VITE_SIGNALR_URL ?? "",
    cookieDomain: ".dncscrub.com",
    classicUrl: "https://classic.dncscrub.com",
    tclApiUrl: "https://www.dncscrub.com",
    coreUrl: "https://core.dncscrub.com",
    dataApiUrl: "https://dataapi.dncscrub.com",
  };
}

// True for the Sonera brand domains — dev/staging (.dev) and production (.co),
// apex or any subdomain. Single source of truth for "is this a Sonera host?",
// reused by the login branding and the post-login theme gating.
export function isSoneraHost(host: string): boolean {
  const h = host.toLowerCase();
  return (
    h === "sonera.dev" ||
    h.endsWith(".sonera.dev") ||
    h === "sonera.co" ||
    h.endsWith(".sonera.co")
  );
}

// All brand domains reverse-proxy the legacy hosts under their own apex so cookies
// stay first-party (see infra: App Gateway Set-Cookie/Location rewrite rules), and
// Tcl/gateway allows the SPA origin via CORS. Only the base domain differs between
// sonera.dev, the production sonera.co, and the legacy dnctopco.dev.
function brandConfig(base: string): RuntimeConfig {
  return {
    apiUrl: `https://dnc-api.${base}`,
    signalRUrl: `https://dnc-api.${base}`,
    cookieDomain: `.${base}`,
    classicUrl: `https://dnc-classic.${base}`,
    tclApiUrl: `https://dnc-classic.${base}`,
    coreUrl: `https://dnc-core.${base}`,
    // Data API is cookieless (AllowAnyOrigin CORS, credentials omitted) — no proxy.
    dataApiUrl: "https://dataapi.dncscrub.com",
  };
}

// Map a production hostname to its deployment config. Matching is
// case-insensitive and dot-bounded so similarly-suffixed domains
// (e.g. "evildncscrub.com") do NOT match "*.dncscrub.com".
export function resolveRuntimeConfig(host: string): RuntimeConfig {
  const h = host.toLowerCase();
  // Sonera brand domains: .co is production, .dev is dev/staging.
  if (h === "sonera.co" || h.endsWith(".sonera.co")) {
    return brandConfig("sonera.co");
  }
  if (h === "sonera.dev" || h.endsWith(".sonera.dev")) {
    return brandConfig("sonera.dev");
  }
  // dnctopco.dev was the pre-rename (Sonera) domain — kept until its DNS is retired.
  if (h === "dnctopco.dev" || h.endsWith(".dnctopco.dev")) {
    return brandConfig("dnctopco.dev");
  }
  if (h === "dncscrub.com" || h.endsWith(".dncscrub.com")) {
    return {
      apiUrl: "https://webapi.dncscrub.com",
      signalRUrl: "https://webapi.dncscrub.com",
      cookieDomain: ".dncscrub.com",
      classicUrl: "https://classic.dncscrub.com",
      // www, not classic: the SPA is served from www.dncscrub.com, so this
      // keeps the Tcl XHR endpoints same-origin (their original behavior).
      tclApiUrl: "https://www.dncscrub.com",
      coreUrl: "https://core.dncscrub.com",
      dataApiUrl: "https://dataapi.dncscrub.com",
    };
  }
  // Unknown host → build-time env (.env).
  return envConfig();
}

export const runtimeConfig: RuntimeConfig = import.meta.env.DEV
  ? envConfig()
  : resolveRuntimeConfig(
      typeof window !== "undefined" ? window.location.hostname : "localhost",
    );
